import React, { useState } from 'react';

import { showModal } from 'modal.react.js';
import { isNil } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { buildGuestActionMessage, insertGuestName } from 'components/Pages/Admin/GuestPortal/GuestForms/functions';
import area from 'components/shared/GlobalModalArea/area';
import AlertModal from 'components/shared/GlobalModalArea/Modal/AlertModal';
import { ITINERARIES_ITEM_TYPES } from 'constants/index';
import { useSnackbar } from 'hooks/useSnackbar';
import i18n from 'i18n';
import { guestPortalIdSelector } from 'store/app/selectors';
import { fetchGuestPortalThunk, updateGuestPortalThunk } from 'store/itinerariesItem/thunks';
import { closeModalAction, openModalAction } from 'store/modal';
import { isModalOpenedSelector } from 'store/modal/selectors';
import { accountIdSelector } from 'store/user/selectors';
import axiosApi from 'utils/axios';
import isNoData from 'utils/isNoData';
import { useItinerariesItemContext } from 'utils/itineraries/context';
import { useItinerariesItemStepPath } from 'utils/itineraries/functions/useItinerariesItemStepPath';
import { GUEST_PORTAL_STEPS } from 'utils/itineraries/guestPortal/map';
import logger from 'utils/logger';

import { Alert } from '../../../../../../components/shared/Alert';
import s from './index.module.css';

const GUEST_FORM = 'GUEST_FORM';

export const useGuestForms = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isModalOpened = useSelector(isModalOpenedSelector(GUEST_FORM));
  const onSnackbar = useSnackbar();
  const getStepPath = useItinerariesItemStepPath(ITINERARIES_ITEM_TYPES.GUEST_PORTAL);
  const itinerariesItemContext = useItinerariesItemContext();
  const isInsideItinerariesItemBuilder = !isNil(itinerariesItemContext);
  const accId = useSelector(accountIdSelector);
  const guestPortalId = useSelector(guestPortalIdSelector);
  const [isSendingGuestForms, setIsSendingGuestForms] = useState(false);
  const [isPristine, setIsPristine] = useState(true);

  const closeModal = () => {
    setIsPristine(true);
    dispatch(closeModalAction());
  };

  const openModal = (guest, mode) => {
    dispatch(openModalAction({ type: GUEST_FORM, data: { guest, mode } }));
  };

  const onFormDataUpdated = ({ pristine }) => {
    setIsPristine(pristine);
  };

  const goToGuestFormsTab = (guestId) => {
    return showModal(area, (res) => (
      <AlertModal
        headerText="Edit Guest"
        confirmButtonText="Go to Guest Forms Tab"
        closeButtonText="Cancel"
        footerClassName={s.goGuestFormTabModalFooter}
        bodyText={
          <>
            Edits to this page can only be done in the Guest Forms Tab. <br /> Do you want to go there now?
          </>
        }
        res={res}
        onAccept={() => {
          res();
          history.push(
            getStepPath(GUEST_PORTAL_STEPS.GUEST_FORMS, itinerariesItemContext.itemId, undefined, { guestId }),
          );
        }}
      />
    ));
  };

  const createGuest = async (guest) => {
    try {
      const { data: newGuest } = await axiosApi.post(`/acc/${accId}/guest_portals/${guestPortalId}/guests`, guest);
      await dispatch(fetchGuestPortalThunk(guestPortalId));
      onSnackbar(buildGuestActionMessage('added')(newGuest, true));
      closeModal();
    } catch (err) {
      onSnackbar("Couldn't create guest.", true);
      throw err;
    }
  };
  const updateGuest = async (guest) => {
    try {
      const { data: updatedGuest } = await axiosApi.patch(
        `/acc/${accId}/guest_portals/${guestPortalId}/guests/${guest.id}`,
        guest,
      );
      await dispatch(fetchGuestPortalThunk(guestPortalId));
      onSnackbar(buildGuestActionMessage('updated')(updatedGuest, true));
      closeModal();
    } catch (err) {
      onSnackbar("Couldn't update guest.", true);
      throw err;
    }
  };
  const deleteGuest = async (guest) => {
    try {
      await axiosApi.delete(`/acc/${accId}/guest_portals/${guestPortalId}/guests/${guest.id}`);
      await dispatch(fetchGuestPortalThunk(guestPortalId));
      onSnackbar(buildGuestActionMessage('deleted')(guest, true));
    } catch (err) {
      logger.error(err);
      onSnackbar("Couldn't delete guest.", true);
    }
  };

  const handleCreateGuest = () => {
    if (isInsideItinerariesItemBuilder) {
      return goToGuestFormsTab();
    }

    openModal({}, 'create');
  };
  const handleUpdateGuest = (guest) => {
    if (isInsideItinerariesItemBuilder) {
      return goToGuestFormsTab(guest.id);
    }

    openModal(guest, 'update');
  };
  const handleDeleteGuest = (guest) => {
    if (isInsideItinerariesItemBuilder) {
      return goToGuestFormsTab(guest.id);
    }

    return showModal(area, (res) => (
      <AlertModal
        headerText={i18n.t('proposal_content.guest_portal.guest_form.popup_delete.title')}
        confirmButtonText={i18n.t('common.buttons.delete')}
        closeButtonText={i18n.t('common.buttons.cancel')}
        bodyText={i18n.t('proposal_content.guest_portal.guest_form.popup_delete.body', {
          guest_name: insertGuestName(guest, { leftSpace: true }),
        })}
        res={res}
        onAccept={async () => {
          await deleteGuest(guest);
          res();
        }}
      />
    ));
  };

  const showSuccessSubmitMessage = () => {
    return showModal(area, (res) => (
      <AlertModal
        headerText={i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.popup_success.title')}
        confirmButtonText={i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.popup_success.confirm')}
        res={res}
        hideCloseButton
        bodyText={i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.popup_success.body')}
        onAccept={res}
      />
    ));
  };

  const sendGuestForms = () => {
    if (isInsideItinerariesItemBuilder) {
      return goToGuestFormsTab();
    }

    return showModal(area, (res) => (
      <AlertModal
        headerText={i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.modal.title')}
        confirmButtonText={i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.modal.options.notify')}
        res={res}
        closeButtonText={i18n.t('common.buttons.cancel')}
        bodyText={
          <>
            <p className={s.notifyModalSubtitle}>
              {i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.modal.description')}
            </p>
            <p className={s.notifyModalText}>
              {i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.modal.subdescription')}
            </p>
            <Alert
              variant="info"
              content={i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.modal.splash')}
            />
          </>
        }
        onAccept={async () => {
          try {
            setIsSendingGuestForms(true);
            await axiosApi.put(`/acc/${accId}/guest_portals/${guestPortalId}/submit_guests`);
            setIsSendingGuestForms(false);
            showSuccessSubmitMessage();
            res();
          } catch (err) {
            setIsSendingGuestForms(false);
            logger.error(err);
            onSnackbar('Something went wrong. Please try again later.', true);
          }
        }}
      />
    ));
  };

  const closeGuestForm = () => {
    if (isPristine) {
      closeModal();
      return;
    }

    return showModal(area, (res) => (
      <AlertModal
        headerText={i18n.t('proposal_content.guest_portal.guest_form.popup.title')}
        confirmButtonText={i18n.t('common.buttons.confirm')}
        res={res}
        closeButtonText={i18n.t('common.buttons.cancel')}
        bodyText={
          <>
            {i18n.t('proposal_content.guest_portal.guest_form.popup.are_you_sure')}
            <br /> {i18n.t('proposal_content.guest_portal.guest_form.popup.unsaved_changes')}
          </>
        }
        onAccept={() => {
          res();
          closeModal();
        }}
        footerClassName={s.footer}
      />
    ));
  };

  const checkFieldAvailability = () => {
    if (isInsideItinerariesItemBuilder) {
      return goToGuestFormsTab();
    }
  };

  const updateGuestPortal = async ({ additionalInfo, specialOccasions }, shouldUpdateGlobalStore) => {
    await dispatch(
      updateGuestPortalThunk(
        guestPortalId,
        {
          additionalInfo: isNoData(additionalInfo)
            ? additionalInfo
            : additionalInfo.map((info) => ({ id: info.id, answer: info.answer })), // updating only answer, so it will reduce problems with the case when several users work on the same doc
          specialOccasions,
        },
        shouldUpdateGlobalStore,
      ),
    );
  };

  return {
    handleDeleteGuest,
    handleUpdateGuest,
    handleCreateGuest,

    createGuest,
    updateGuest,

    updateGuestPortal,

    sendGuestForms,
    isSendingGuestForms,

    closeModal,
    closeGuestForm,
    checkFieldAvailability,
    isModalOpened,
    additionalInputProps: isInsideItinerariesItemBuilder ? { readOnly: 'readonly' } : {},
    isInsideItinerariesItemBuilder,
    onFormDataUpdated,
  };
};
